import React, { useState } from "react";

import { formatNumber, useGetFarms, useTranslation, useUserContext } from "utils";

import { Drawer, HeaderItemSwitcher, IconButton, TooltipWrapper } from "components";

import { FarmDetails } from "pages/app/farm/components/FarmDetails";

export const FarmSwitcher = () => {
  const { t } = useTranslation(["dashboard", "onboarding", "farm"]);
  const { data: farms, currentFarm, isLoading, refetch: refetchFarms } = useGetFarms();
  const { switchFarm, reloadUserData } = useUserContext();

  const [showAddFarmDrawer, setShowAddFarmDrawer] = useState(false);
  const [editFarmId, setEditFarmId] = useState<string | null>(null);

  const handleFarmSelection = (farmId: string) => {
    setEditFarmId(null);
    switchFarm(farmId);
    reloadUserData();
  };

  const handleFarmAdded = () => {
    setShowAddFarmDrawer(false);
    refetchFarms();
  };

  const handleOpenAddFarmDrawer = () => {
    setEditFarmId(null);
    setShowAddFarmDrawer(true);
  };

  const handleOpenEditFarmDrawer = (farmId: string) => {
    setShowAddFarmDrawer(false);
    setEditFarmId(farmId);
  };

  const handleFarmEdited = () => {
    setEditFarmId(null);
    refetchFarms();
    reloadUserData();
  };

  return (
    <>
      <HeaderItemSwitcher
        dataTestId="farm-switcher-selection"
        items={farms.map((farm) => ({
          id: farm.id,
          label: farm.name,
          subLabel: `${formatNumber({ number: farm.size ?? 0 })} ${t("farm:hectares")}`,
          actionIcon:
            farm.id === currentFarm?.id ? (
              <TooltipWrapper tooltip={{ title: t("farm:farmContextMenu.editFarm") }}>
                <IconButton
                  dataTestId="farm-switcher-edit-action"
                  iconName="tabler/edit"
                  size="small"
                  type="tertiary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditFarmDrawer(farm.id);
                  }}
                />
              </TooltipWrapper>
            ) : undefined,
        }))}
        activeItemId={currentFarm?.id}
        isLoading={isLoading}
        onSelect={handleFarmSelection}
        truncateTriggerLabelAt={15}
        footerLabel={t("dashboard:farmCard.addFarm")}
        onFooterClick={handleOpenAddFarmDrawer}
      />
      <Drawer title={t("onboarding:onboardingFormHeader.farm")} onClose={() => setShowAddFarmDrawer(false)} visible={showAddFarmDrawer}>
        <FarmDetails isOnboarding={false} handleBack={() => setShowAddFarmDrawer(false)} selectedFarmId="" onFinish={handleFarmAdded} />
      </Drawer>
      {editFarmId ? (
        <Drawer
          data-testid="farm-switcher-edit-farm-drawer"
          visible={!!editFarmId}
          title={t("onboarding:onboardingFormHeader.editFarm")}
          onClose={() => setEditFarmId(null)}
          footer={undefined}
        >
          <FarmDetails selectedFarmId={editFarmId} onFinish={handleFarmEdited} isOnboarding={false} handleBack={() => setEditFarmId(null)} />
        </Drawer>
      ) : null}
    </>
  );
};
